import React, { useCallback, useMemo } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import AccessAlarmIcon from "@mui/icons-material/AccessAlarm";
import { DataGridPremium, type GridColDef, type GridCellParams } from "@mui/x-data-grid-premium";
import { EuroField, LocalizedNumberField } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import type { ProjectPosition, WorksOnPosition } from "../types/ProjectTypes";

type TeamMemberPositionAssignmentTableProps = {
	assignments: WorksOnPosition[];
	availablePositions: ProjectPosition[];
	onAssignmentsChange: (assignments: WorksOnPosition[]) => void;
	validationErrors?: Record<string, string>;
};

export const TeamMemberPositionAssignmentTable = (
	props: TeamMemberPositionAssignmentTableProps,
) => {
	const { t } = useTranslation();
	const { assignments, availablePositions, onAssignmentsChange, validationErrors } = props;

	const updateAssignment = useCallback(
		(positionId: string, field: "pricePerHour" | "hours", value: number | null) => {
			const updated = assignments.map((a) =>
				a.positionId === positionId ? { ...a, [field]: value } : a,
			);
			onAssignmentsChange(updated);
		},
		[assignments, onAssignmentsChange],
	);

	const removeAssignment = useCallback(
		(positionId: string) => {
			const updated = assignments.filter((a) => a.positionId !== positionId);
			onAssignmentsChange(updated);
		},
		[assignments, onAssignmentsChange],
	);

	const columns: GridColDef<WorksOnPosition>[] = useMemo(
		() => [
			{
				field: "position",
				headerName: t("position"),
				flex: 1,
				minWidth: 150,
				renderCell: (params: GridCellParams) => {
					const position = availablePositions.find((p) => p.id === params.row.positionId);
					return position?.service?.name || "-";
				},
			},
			{
				field: "positionHours",
				headerName: t("position-total-hours"),
				width: 180,
				renderCell: (params: GridCellParams) => {
					const position = availablePositions.find((p) => p.id === params.row.positionId);
					return position?.hours?.toFixed(2) || "-";
				},
			},
			{
				field: "hours",
				headerName: t("assigned-hours"),
				width: 180,
				renderCell: (params: GridCellParams) => {
					const hasError = validationErrors?.[params.row.positionId]?.includes("hours");
					return (
						<Box
							sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}
							data-testid={`assignment-hours-${params.row.positionId}`}>
							<LocalizedNumberField
								value={params.row.hours ?? null}
								onValueChange={(value) => {
									updateAssignment(params.row.positionId, "hours", value);
								}}
								icon={<AccessAlarmIcon />}
								iconBackgroundColor="#1a455c"
								size="small"
								error={hasError}
								allowNegative={false}
							/>
						</Box>
					);
				},
			},
			{
				field: "pricePerHour",
				headerName: t("hourly-rate"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					const hasError = validationErrors?.[params.row.positionId]?.includes("price");
					return (
						<Box
							sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%" }}
							data-testid={`assignment-rate-${params.row.positionId}`}>
							<EuroField
								value={params.row.pricePerHour ?? null}
								onValueChange={(value) => {
									updateAssignment(params.row.positionId, "pricePerHour", value);
								}}
								size="small"
								error={hasError}
								allowNegative={false}
							/>
						</Box>
					);
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				width: 100,
				sortable: false,
				renderCell: (params: GridCellParams) => (
					<IconButton
						onClick={() => removeAssignment(params.row.positionId)}
						size="small"
						color="error">
						<DeleteIcon />
					</IconButton>
				),
			},
		],
		[t, availablePositions, updateAssignment, removeAssignment, validationErrors],
	);

	return (
		<Box sx={{ width: "100%", mt: 2 }} data-testid="position-assignment-table">
			<Typography variant="h6" gutterBottom>
				{t("position-assignments")}
			</Typography>
			<DataGridPremium
				rows={assignments}
				columns={columns}
				getRowId={(row) => row.positionId}
				autoHeight
				disableRowSelectionOnClick
				hideFooter
			/>
		</Box>
	);
};
